import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      setUser(null);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const applySession = (data) => {
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    setUser(data);
    return data;
  };

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    return applySession(data);
  };

  // Demo roles only, and only where the deployment enabled it. No password is
  // sent -- the server decides who is eligible.
  const demoLogin = async (username) => {
    const { data } = await api.post('/auth/demo-login', { username });
    return applySession(data);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* ignore */ }
    localStorage.removeItem('access_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, demoLogin, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
